import React from 'react';
import {isUrl} from '@packages/utilities/string-utils';
import {withMeta, withSystem, Plugins, Atom} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {qualify, iconName, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TBoolean,
  TText,
  TRecord,
  TEnum,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';

// eslint-disable-next-line webflow/module-top-level-imports
import {withLlmRender} from '@packages/utilities/llm-wfdl-renderer/withLlmRender';

const getDefaultSize = (
  layout: 'box_count' | 'button_count' | 'standard' | string
) => {
  if (layout === 'box_count')
    return {
      width: 55,
      height: 65,
    };
  if (layout === 'button_count')
    return {
      width: 90,
      height: 20,
    };

  return {
    width: 225,
    height: 35,
  };
};

const addHttpProtocol = (url: string) =>
  /^http/.test(url) ? url : 'http://' + url;

const encodeParam = ([key, val]: [string, string]) =>
  `${key}=${encodeURIComponent(val)}`;

const Facebook = Atom.createRender(
  withLlmRender(({attributes, classes, data, generateAttributes}) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const {widget} = data;

    const facebookClasses = classes.push('w-widget').push('w-widget-facebook');

    const url = isUrl(widget.url)
      ? addHttpProtocol(widget.url)
      : 'https://facebook.com/webflow';
    const layout = widget.layout || 'standard';

    const params: Record<string, string> = {
      href: url,
      layout,
      locale: widget.locale || 'en_US',
      action: 'like',
      show_faces: 'false',
      share: 'false',
    };

    const queryString = Object.entries(params).map(encodeParam).join('&');
    const frameSrc = `//www.facebook.com/plugins/like.php?${queryString}`;
    const defaultSize = getDefaultSize(layout);
    const width = widget.width !== undefined ? widget.width : defaultSize.width;
    const height =
      widget.height !== undefined ? widget.height : defaultSize.height;

    return (
      <div {...generateAttributes(attributes, facebookClasses)}>
        <iframe
          title="Facebook Like Button"
          src={frameSrc}
          scrolling="no"
          frameBorder="0"
          // eslint-disable-next-line react/no-unknown-property
          allowTransparency={'true' as any}
          style={{
            border: 'none',
            overflow: 'hidden',
            width,
            height,
          }}
        />
      </div>
    );
  })
);

const config = createDevlinkAtom({
  displayName: 'Facebook',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Widget'), iconName('Facebook')),
  },
  showEmpty: false,
  render: Facebook,
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    value: withSystem({private: true}, TText),
    widget: TRecord({
      url: withMeta(
        {
          placeholder: 'https://www.facebook.com/webflow',
        },
        withSystem(
          {
            label: 'URL',
            isLocalizable: true,
          },
          TText
        )
      ),
      layout: withSystem(
        {
          label: 'Layout',
          isLocalizable: true,
        },
        TEnum({
          standard: 'Standard',
          box_count: 'Box',
          button_count: 'Button',
        })
      ),
      width: withSystem(
        {
          defaultValue: 225,
          label: 'Width',
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('PXNumber')))
      ),
      height: withSystem(
        {
          defaultValue: 35,
          label: 'Height',
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('PXNumber')))
      ),
      locale: withMeta(
        {variant: 'menu'},
        withSystem(
          {
            defaultValue: 'en_US',
            label: 'Locale',
            isLocalizable: true,
          },
          TEnum({
            af_ZA: 'Afrikaans',
            ar_AR: 'Arabic',
            az_AZ: 'Azerbaijani',
            be_BY: 'Belarusian',
            bg_BG: 'Bulgarian',
            bn_IN: 'Bengali',
            bs_BA: 'Bosnian',
            ca_ES: 'Catalan',
            cs_CZ: 'Czech',
            cx_PH: 'Cebuano',
            cy_GB: 'Welsh',
            da_DK: 'Danish',
            de_DE: 'German',
            el_GR: 'Greek',
            en_GB: 'English (UK)',
            en_PI: 'English (Pirate)',
            en_UD: 'English (Upside Down)',
            en_US: 'English (US)',
            eo_EO: 'Esperanto',
            es_ES: 'Spanish (Spain)',
            es_LA: 'Spanish',
            et_EE: 'Estonian',
            eu_ES: 'Basque',
            fa_IR: 'Persian',
            fb_LT: 'Leet Speak',
            fi_FI: 'Finnish',
            fo_FO: 'Faroese',
            fr_CA: 'French (Canada)',
            fr_FR: 'French (France)',
            fy_NL: 'Frisian',
            ga_IE: 'Irish',
            gl_ES: 'Galician',
            gn_PY: 'Guarani',
            he_IL: 'Hebrew',
            hi_IN: 'Hindi',
            hr_HR: 'Croatian',
            hu_HU: 'Hungarian',
            hy_AM: 'Armenian',
            id_ID: 'Indonesian',
            is_IS: 'Icelandic',
            it_IT: 'Italian',
            ja_JP: 'Japanese',
            ka_GE: 'Georgian',
            km_KH: 'Khmer',
            ko_KR: 'Korean',
            ku_TR: 'Kurdish',
            la_VA: 'Latin',
            lt_LT: 'Lithuanian',
            lv_LV: 'Latvian',
            mk_MK: 'Macedonian',
            ml_IN: 'Malayalam',
            ms_MY: 'Malay',
            nb_NO: 'Norwegian (bokmal)',
            ne_NP: 'Nepali',
            nl_NL: 'Dutch',
            nn_NO: 'Norwegian (nynorsk)',
            pa_IN: 'Punjabi',
            pl_PL: 'Polish',
            ps_AF: 'Pashto',
            pt_BR: 'Portuguese (Brazil)',
            pt_PT: 'Portuguese (Portugal)',
            ro_RO: 'Romanian',
            ru_RU: 'Russian',
            sk_SK: 'Slovak',
            sl_SI: 'Slovenian',
            sq_AL: 'Albanian',
            sr_RS: 'Serbian',
            sv_SE: 'Swedish',
            sw_KE: 'Swahili',
            ta_IN: 'Tamil',
            te_IN: 'Telugu',
            th_TH: 'Thai',
            tl_PH: 'Filipino',
            tr_TR: 'Turkish',
            uk_UA: 'Ukrainian',
            ur_PK: 'Urdu',
            vi_VN: 'Vietnamese',
            zh_CN: 'Simplified Chinese (China)',
            zh_HK: 'Traditional Chinese (Hong Kong)',
            zh_TW: 'Traditional Chinese (Taiwan)',
          })
        )
      ),
      type: withSystem(
        {
          defaultValue: 'facebook',
          private: true,
          label: '',
        },
        TText
      ),
      html: withSystem(
        {
          defaultValue: true,
          private: true,
          label: '',
        },
        TBoolean
      ),
    }),
  },
});

export default config;
